import { Suspense, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { Badge, BadgeColor } from "@/components/catalyst/badge";
import { workOrderRecordRoute } from "@/app/ProDrivers/Common/Routes/proDriverRoutes";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ActivityIcon } from "lucide-react";
import {
  InvoiceStatus,
  ModelType,
  UpdateInvoiceInput,
  UpsertPayableLineItemInput,
} from "@/gql/graphql";
import { useTranslations } from "use-intl";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { useInvoiceActions } from "@/app/Invoices/Hooks/useInvoiceActions";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { updateInvoiceMutation } from "@/app/Invoices/GraphQL/invoiceMutations";
import {
  invoiceRecordQuery,
  InvoiceRecord,
} from "@/app/Invoices/GraphQL/invoiceRecordQuery";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { PayableLineItemTable } from "@/app/Invoices/Components/ViewInvoiceLineItems";
import { InvoiceLineItemsForm } from "@/app/Invoices/Forms/InvoiceLineItemsForm";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";

const invoiceStatusColorMap: Record<InvoiceStatus, BadgeColor> = {
  [InvoiceStatus.Draft]: "zinc",
  [InvoiceStatus.Issued]: "yellow",
  [InvoiceStatus.Paid]: "green",
  [InvoiceStatus.Voided]: "zinc",
  [InvoiceStatus.Overdue]: "red",
};

type TabId = "details" | "activity";

export function WorkOrderInvoiceDrawer() {
  const navigate = useNavigate();
  const search = useSearch({
    from: workOrderRecordRoute.id,
  });
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const isOpen = !!search.invoiceId;

  function handleClose() {
    navigate({
      to: ".",
      search: (prev: { invoiceId?: string }) => ({
        ...prev,
        invoiceId: undefined,
      }),
    });
  }

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <DrawerOverlay />
      <DrawerContent showCloseButton={true} onClose={handleClose}>
        {search.invoiceId ? (
          <div className="flex h-full flex-col overflow-hidden">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Spinner />
                </div>
              }
            >
              <InvoiceSheetContent
                invoiceId={search.invoiceId}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            </Suspense>
          </div>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

function InvoiceSheetContent({
  invoiceId,
  activeTab,
  setActiveTab,
}: {
  invoiceId: string;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const { data } = useSuspenseGqlQuery(invoiceRecordQuery, { id: invoiceId });
  const invoice = data.invoice;

  if (!invoice) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Invoice not found</p>
      </div>
    );
  }

  return (
    <>
      <InvoiceSheetHeader
        invoice={invoice}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <DrawerBody>
        <TabContent invoice={invoice} activeTab={activeTab} />
      </DrawerBody>
    </>
  );
}

function TabContent({
  invoice,
  activeTab,
}: {
  invoice: NonNullable<InvoiceRecord>;
  activeTab: TabId;
}) {
  switch (activeTab) {
    case "details":
      return <InvoiceDetailsTab invoice={invoice} />;
    case "activity":
      return <InvoiceActivityTab invoice={invoice} />;
    default:
      return null;
  }
}

function InvoiceSheetHeader({
  invoice,
  activeTab,
  setActiveTab,
}: {
  invoice: NonNullable<InvoiceRecord>;
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const t = useTranslations("invoice");
  const getActions = useInvoiceActions();

  return (
    <DrawerHeader>
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
            <DocumentTextIcon className="h-6 w-6 text-gray-500" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <DrawerTitle>{invoice.reference}</DrawerTitle>
              <Badge color={invoiceStatusColorMap[invoice.status]}>
                {t(`status.${invoice.status}`)}
              </Badge>
            </div>
            <DrawerDescription>
              {formatCurrency(invoice.amount_inc_tax, invoice.currency)}
            </DrawerDescription>
          </div>
        </div>
        <ActionGroupButtons buttons={getActions(invoice)} />
      </div>
      <div className="px-6">
        <InvoiceTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </DrawerHeader>
  );
}

function InvoiceTabs({
  activeTab,
  setActiveTab,
}: {
  activeTab: TabId;
  setActiveTab: (tab: TabId) => void;
}) {
  const tabs = [
    {
      id: "details" as const,
      name: "Details",
      Icon: DocumentTextIcon,
    },
    {
      id: "activity" as const,
      name: "Activity",
      Icon: ActivityIcon,
    },
  ];

  return (
    <div className="flex gap-1">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          className={`group relative flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-all duration-200 ease-in-out ${
            activeTab === tab.id
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`}
        >
          <tab.Icon
            className={`h-5 w-5 flex-shrink-0 transition-colors duration-200 ${
              activeTab === tab.id
                ? "text-indigo-600"
                : "text-gray-400 group-hover:text-gray-500"
            }`}
          />
          <span>{tab.name}</span>
        </button>
      ))}
    </div>
  );
}

function InvoiceDetailsTab({
  invoice,
}: {
  invoice: NonNullable<InvoiceRecord>;
}) {
  const t = useTranslations("invoice");
  const { mutateAsync } = useGqlMutation(updateInvoiceMutation);
  const { isAdmin } = useTenant();

  return (
    <div className="space-y-6">
      {/* Invoice Details Card */}
      <RecordScreenFormCard<UpdateInvoiceInput>
        title="Details"
        isEditable={isAdmin}
        initialValues={{
          invoice_date: invoice.invoice_date,
          due_date: invoice.due_date,
          purchase_order_number: invoice.purchase_order_number,
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: invoice.id,
            input: values,
          });
        }}
        view={
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Reference</dt>
              <dd className="font-medium text-gray-900">{invoice.reference}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd>
                <Badge color={invoiceStatusColorMap[invoice.status]}>
                  {t(`status.${invoice.status}`)}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Invoice Date</dt>
              <dd className="font-medium text-gray-900">
                {invoice.invoice_date ? formatDate(invoice.invoice_date) : "-"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Due Date</dt>
              <dd className="font-medium text-gray-900">
                {invoice.due_date ? formatDate(invoice.due_date) : "-"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">PO Number</dt>
              <dd className="font-medium text-gray-900">
                {invoice.purchase_order_number || "-"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Payee</dt>
              <dd className="font-medium text-gray-900">
                {invoice.payee.name}
              </dd>
            </div>
          </dl>
        }
      >
        <DateInput name="invoice_date" label="Invoice Date" />
        <DateInput name="due_date" label="Due Date" />
        <TextInput
          name="purchase_order_number"
          label="Purchase Order Number"
          optionalLabel
        />
      </RecordScreenFormCard>

      {/* Line Items Card */}
      <RecordScreenFormCard<UpdateInvoiceInput>
        title="Line Items"
        isEditable={isAdmin}
        enableReinitialize={true}
        initialValues={{
          lineItems: {
            upsert: invoice.lineItems.map((lineItem) => ({
              id: lineItem.id,
              unit_amount: lineItem.unit_amount,
              quantity: lineItem.quantity,
              description: lineItem.description,
              tax_type: lineItem.tax_type,
              fulfills: lineItem.fulfills
                ? {
                    update: {
                      id: lineItem.fulfills?.id ?? null!,
                    },
                  }
                : undefined,
            })) as UpsertPayableLineItemInput[],
            delete: [],
          },
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: invoice.id,
            input: values,
          });
        }}
        view={<PayableLineItemTable invoice={invoice} />}
      >
        <InvoiceLineItemsForm
          payeeId={invoice.payee.id}
          currency={invoice.currency}
          invoice={invoice}
        />
      </RecordScreenFormCard>
    </div>
  );
}

function InvoiceActivityTab({
  invoice,
}: {
  invoice: NonNullable<InvoiceRecord>;
}) {
  return <ActivityLogFeed modelId={invoice.id} modelType={ModelType.Invoice} />;
}
